/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Box, Button, Divider, Flex, Select, TextInput, createForm } from '@applyboard/crystal-ui';
import { useNavigate } from 'react-router-dom';
import { CreateProfileProps, ProfileData, App } from '../../clients';
import { validateEmailFormat } from '../../utils';
import { userTypeToPlatformUserTypeOptions } from '../../utils/shared';
import { useProfilePermissions, useProfileForm, AddProfileFieldValues } from '../../hooks/profile';
import { AppAccessSection, AppConfig, UserStateActions, UserWithState } from '../';

export type ProfileFormProps = {
  currentSessionUser: ProfileData;
  profileDataToBeEdited?: ProfileData;
  onSubmit: (profileData: CreateProfileProps | Partial<CreateProfileProps>) => void;
  isLoading: boolean;
};

const { Form, Field } = createForm<AddProfileFieldValues>();

export function ProfileForm({
  currentSessionUser,
  profileDataToBeEdited,
  onSubmit,
  isLoading,
}: ProfileFormProps): JSX.Element {
  const navigate = useNavigate();

  const permissions = useProfilePermissions({
    currentSessionUser,
    profileDataToBeEdited,
  });

  const { initialValues, handleSubmit } = useProfileForm({
    profileDataToBeEdited,
    currentSessionUser,
    onSubmit,
  });

  // Configure apps for the shared AppAccessSection
  const profileApps: AppConfig[] = [
    {
      appName: App.AMS,
      appLabel: 'Application Management',
      logoSrc: '/ams-logo.png',
    },
    {
      appName: App.EnrolmentPlanner,
      appLabel: 'Enrolment Planner',
      logoSrc: '/enrolment-planner.svg',
    },
  ];

  return (
    <>
      <Form onSubmit={handleSubmit} defaultValues={initialValues}>
        <Flex py={{ xs: 2, sm: 8 }} justify="between" align="center" gap={10} grow={1}>
          <Flex direction="column" basis="80%">
            <Box pb={8}>
              <Field
                aria-label="email"
                // @ts-ignore
                as={TextInput}
                disabled={profileDataToBeEdited !== undefined}
                label="Email"
                name="email"
                required="Email is required."
                validate={(email: string) => (validateEmailFormat(email) ? true : 'This email is invalid.')}
              />
            </Box>
            <Flex pb={8} gap={10}>
              <Flex.Item basis={{ xs: '100%', sm: '50%' }}>
                <Field
                  // @ts-ignore
                  as={TextInput}
                  disabled={!permissions.canEditProfile}
                  aria-label="givenName"
                  label="Given Name"
                  name="givenName"
                  required="Given name is required."
                />
              </Flex.Item>
              <Flex.Item basis={{ xs: '100%', sm: '50%' }}>
                <Field
                  // @ts-ignore
                  as={TextInput}
                  disabled={!permissions.canEditProfile}
                  aria-label="familyName"
                  label="Family Name"
                  name="familyName"
                  required="Family name is required."
                />
              </Flex.Item>
            </Flex>
            {permissions.canEditUserTypeAndApps && (
              <>
                <Flex grow={1} pb={8}>
                  <Flex.Item basis={{ xs: '100%', sm: '50%' }}>
                    <Field
                      appearance="native"
                      as={Select}
                      name="userType"
                      placeholder="Select"
                      label="Platform User Type"
                      required="Enter a platform user type."
                      disabled={permissions.isEditingOwnProfile}
                      children={userTypeToPlatformUserTypeOptions(currentSessionUser.userType).map((option) => (
                        <Select.Option value={option.value} label={option.label} key={option.value} />
                      ))}
                    />
                  </Flex.Item>
                </Flex>
                <AppAccessSection
                  isDisabled={permissions.isEditingOwnProfile}
                  Field={Field}
                  apps={profileApps}
                  noticeType="profile"
                />
              </>
            )}
            <Box py={{ xs: 8, sm: 10 }}>
              <Divider />
            </Box>
            <Flex justify="end" gap={4}>
              <UserStateActions
                userData={profileDataToBeEdited as UserWithState}
                canManageUserState={permissions.canManageProfileState}
                redirectPath="/profiles/school-users"
                userType="User"
              />
              <Button
                type="button"
                onClick={() => {
                  navigate('/profiles/school-users');
                }}
                intent="primary"
                emphasis="outlined"
              >
                Cancel
              </Button>
              <Button loading={isLoading} disabled={!permissions.canEditProfile} type="submit" intent="primary">
                {`${profileDataToBeEdited ? 'Edit' : 'Add'} User`}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Form>
    </>
  );
}
